import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { ROLE_OPTION } from '../../../../utils/spds/constants/aria';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Button from '../button.gjs';
import Super from '../base.gjs';
import styles from './item.module.scss';

const METADATA = Metadata.define({
  description:
    'Menu item component used within menus. Renders with `tabindex="-1"` so arrow keys from `Spds::Core::KeyboardNavigable` move focus rather than sequential Tab.',
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_OPTION }]),
    pick(ACTION_ARGS, [
      {
        name: 'onClick',
        description: 'Action to be called when the menu item is clicked.',
        required: true,
      },
    ]),
    pick(COMPONENT_STATE_ARGS, ['disabled', 'hidden']),
  ],
  meta: {
    retainSelection: Arg.boolean({
      description: 'Indicates whether item will remain selected after second click',
    }),
    showSelectedStyles: Arg.boolean({
      default: true,
      description:
        'Determines whether to apply styles for selected state when the item is selected',
    }),
    value: Arg.string({ description: 'The value associated with the menu item.' }),
    wrappedContent: Arg.boolean({
      description: 'If true, wraps the yielded content in a <span> for additional styling control.',
    }),
  },
  name: 'Menu Item',
});

export default class SpdsCoreMenuItem extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-item';
  classesToMap = ['hidden'];
  styles = styles;

  @tracked isSelected = 'false';

  @reads('baseElement.ariaSelected') ariaSelected;

  get keepSelectedState() {
    return this.ariaSelected === 'true' && this.args.retainSelection;
  }

  get showSelectedStyles() {
    return this.argOrDefault('showSelectedStyles');
  }

  get role() {
    return this.argOrDefault('role');
  }

  @action
  onClick(event) {
    if (this.showSelectedStyles && !this.keepSelectedState) {
      this.isSelected = `${this.ariaSelected === 'false'}`;
    }

    let host = this.baseElement ?? event.currentTarget;

    // Event.target is a getter-only accessor, so a copy cannot reassign it. A Proxy reports the
    // item as the target when the click lands on nested content (e.g. a search-match <strong>)
    // while forwarding the rest of the native click.
    this.args.onClick(
      new Proxy(event, {
        get(evt, prop) {
          if (prop === 'target') return host;

          let value = evt[prop];

          return typeof value === 'function' ? value.bind(evt) : value;
        },
      })
    );
  }

  <template>
    <Button
      @flex="1"
      @gap="0"
      aria-current={{this.isSelected}}
      aria-selected={{this.isSelected}}
      class={{this.classes}}
      data-test-spds-menu-item
      data-value={{@value}}
      id={{this.id}}
      role={{this.role}}
      tabindex="-1"
      type="button"
      ...attributes
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{on "click" this.onClick}}
    >
      {{#if @wrappedContent}}
        <span>{{yield}}</span>
      {{else}}
        {{yield}}
      {{/if}}
    </Button>
  </template>
}

export const SPDS_CORE_MENU_ITEM_METADATA = SpdsCoreMenuItem.metadata;
